import { useState } from "react";
import styled from "styled-components";
import { CopyToClipboard } from "react-copy-to-clipboard";

import { SkedIcon, TooltipWrapper } from "ui";
import { externalReviewLinkMapper } from "utils/externalReview";
import {
  ApprovalLink,
  ApprovalTheme,
  useApprovalLinkMutations,
} from "pages/approvals/approval-queries";

import { CollaborationPostStatus } from "../../../features/user/types";

import { AccountAvatar } from "@/entities/accounts/components/account/avatar";
import { AccountType } from "shared/types/accounts";

// Bootstrap ControlLabel replacement
const ControlLabel = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: 700;
`;

type Props = {
  link: ApprovalLink;
  statuses: CollaborationPostStatus[];
  accounts: AccountType[];
  themes: Record<string, ApprovalTheme>;
  onEdit: (link: ApprovalLink) => void;
  isEditMode: boolean;
};

const ExternalCollabLink = ({
  link,
  statuses,
  accounts,
  themes,
  onEdit,
  isEditMode,
}: Props) => {
  const { deleteLink } = useApprovalLinkMutations();
  const reviewStatus = statuses.find((s) => s.key === link.showPostWithState);
  const rejectStatus = statuses.find((s) => s.key === link.rejectState);
  const approveStatus = statuses.find((s) => s.key === link.acceptState);
  const theme = link.themeId ? themes[link.themeId] : undefined;
  const linkUrl = externalReviewLinkMapper(link.slugId ?? "");

  const [copyIconHovered, setCopyIconHovered] = useState(false);

  const errorClass = "text-base text-red-800";

  const accessibleAccounts = accounts.filter((acc) =>
    (link.accountIds ?? []).includes(acc._id)
  );
  if (accessibleAccounts.length === 0) {
    return <></>;
  }

  const isConversationsEnabled =
    link.isStatusChangesVisible !== false ||
    link.isExternalNotesVisible !== false ||
    link.isPostActivityVisible !== false;

  return (
    <div data-testid={`externalCollabLink-${link._id}`}>
      <LinkTable>
        <thead>
          <tr>
            <th>Link Details</th>
            <th>States</th>
            <th>Review URL</th>
            <th className="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <Col>
                <ControlLabel className="title">Link name:</ControlLabel>
                <ControlLabel className="value">
                  {link.linkName}
                  {link.isReadOnly && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-neutral-100 text-neutral-600 rounded">
                      View Only
                    </span>
                  )}
                </ControlLabel>
              </Col>
            </td>
            <td>
              <></>
            </td>
            <td>
              <Row>
                <Slug>
                  <a href={linkUrl} target="_blank" rel="noopener noreferrer">
                    {link.slugId}
                  </a>
                </Slug>
                <CopyToClipboard text={linkUrl}>
                  <div
                    onMouseDown={() => setCopyIconHovered(true)}
                    onMouseUp={() => setCopyIconHovered(false)}
                  >
                    <SkedIcon
                      icon={"copy"}
                      size="18"
                      tooltip="Copy URL"
                      color={copyIconHovered ? "purple" : "black"}
                    />
                  </div>
                </CopyToClipboard>
              </Row>
            </td>
            <td>
              {!isEditMode && (
                <div className="flex w-full justify-end items-center">
                  <SkedIcon
                    className="mr-2"
                    icon="icon-pencil"
                    size="20"
                    color="purple"
                    tooltip="Edit link"
                    onClick={() => onEdit(link)}
                  />
                  <SkedIcon
                    className="mr-2"
                    icon="trash"
                    size="20"
                    color="red"
                    tooltip="Delete link"
                    onClick={() => {
                      if (
                        confirm("Are you sure you want to delete this link?")
                      ) {
                        deleteLink.mutate(link._id);
                      }
                    }}
                  />
                </div>
              )}
            </td>
          </tr>
          <tr>
            <td>
              <Col>
                {!link.isReadOnly && (
                  <>
                    <ControlLabel className="title mb-1">
                      Messages and comments:
                    </ControlLabel>
                    <ControlLabel
                      className="value"
                      style={{
                        color: isConversationsEnabled ? "#66b913" : "#fa4f38",
                      }}
                    >
                      {isConversationsEnabled
                        ? "Viewer can view comments"
                        : "Viewer can NOT view comments"}
                    </ControlLabel>
                  </>
                )}

                {!link.isReadOnly && (
                  <>
                    <ControlLabel className="title mb-1">
                      Review enabled:
                    </ControlLabel>
                    <ControlLabel
                      className="value"
                      style={{
                        color: "#66b913",
                      }}
                    >
                      Viewer can review posts & drafts
                    </ControlLabel>
                  </>
                )}

                <ControlLabel className="title mb-1">Link theme:</ControlLabel>
                <ControlLabel className="value">{theme?.name}</ControlLabel>
                {!link.isReadOnly && (
                  <>
                    <ControlLabel className="title mb-1">
                      Reviewer:
                    </ControlLabel>
                    <ControlLabel className="value">
                      {link.userName}
                    </ControlLabel>
                  </>
                )}
              </Col>
            </td>
            <td>
              <Row content={"space-between"}>
                <Col>
                  <ControlLabel className="title">
                    {link.isReadOnly
                      ? "Status for displayed posts"
                      : "Posts to review"}
                    :
                  </ControlLabel>
                  {reviewStatus && (
                    <ControlLabel className="value">
                      <Indicator color={reviewStatus?.color} />{" "}
                      <span>{reviewStatus?.label}</span>
                    </ControlLabel>
                  )}
                  {!reviewStatus && (
                    <div className={errorClass}>
                      Review status no longer valid (status is deleted) - please
                      edit and re-save.
                    </div>
                  )}
                  {!link.isReadOnly && (
                    <>
                      <ControlLabel className="title">
                        On approval:
                      </ControlLabel>
                      {approveStatus && (
                        <ControlLabel className="value">
                          <Indicator color={approveStatus?.color} />{" "}
                          <span>{approveStatus?.label}</span>
                        </ControlLabel>
                      )}
                      {!approveStatus && (
                        <ControlLabel className="value">
                          Approval status for this link no longer valid
                          (deleted). Please edit and select a status.
                        </ControlLabel>
                      )}
                      {link.notifications?.onAccept?.length ? (
                        <>
                          <ControlLabel className="title">
                            Will notify:
                          </ControlLabel>
                          <ControlLabel className="value">
                            <NotifyList
                              emails={link.notifications.onAccept}
                            ></NotifyList>
                          </ControlLabel>
                        </>
                      ) : (
                        ""
                      )}
                      <ControlLabel className="title">
                        On rejection:
                      </ControlLabel>
                      {rejectStatus && (
                        <ControlLabel className="value">
                          <Indicator color={rejectStatus?.color} />{" "}
                          <span>{rejectStatus?.label}</span>
                        </ControlLabel>
                      )}
                      {!rejectStatus && (
                        <ControlLabel className="value">
                          Reject status for this link no longer valid (deleted).
                          Please edit and select a status.
                        </ControlLabel>
                      )}
                      {link.notifications?.onReject?.length ? (
                        <>
                          <ControlLabel className="title">
                            Will notify:
                          </ControlLabel>
                          <ControlLabel className="value">
                            <NotifyList
                              emails={link.notifications.onReject}
                            ></NotifyList>
                          </ControlLabel>
                        </>
                      ) : (
                        ""
                      )}
                    </>
                  )}
                </Col>
              </Row>
            </td>
          </tr>
        </tbody>
      </LinkTable>

      <AccountsCol>
        <ControlLabel>Accounts:</ControlLabel>
        <AccountsRow>
          {accessibleAccounts.map((acc) => {
            return (
              <Row key={acc._id}>
                <div className="mr-1">
                  <AccountAvatar size="small" account={acc} useYtRedPlayIcon />
                </div>
                <span>{acc.loginAlias || acc.login}</span>
              </Row>
            );
          })}
        </AccountsRow>
      </AccountsCol>

      <BreakLine />
    </div>
  );
};

const NotifyList = ({ emails }: { emails: string[] }) => {
  const extras = emails.slice(1);

  return (
    <>
      {emails[0]}
      {extras.length > 0 ? (
        <TooltipWrapper
          placement="bottom"
          tooltip={<div className="text-left">{extras.join(", ")}</div>}
        >
          <span>, +{extras.length}</span>
        </TooltipWrapper>
      ) : null}
    </>
  );
};

const Col = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div<{ content?: string }>`
  display: flex;
  align-items: center;
  justify-content: ${({ content }: any) => content || ""};
`;

const LinkTable = styled.table`
  width: 100%;
  padding-bottom: 16px;
  border-bottom: 1px solid #efefef;

  thead {
    border-bottom: 2px solid #c4c4c4;
  }

  th {
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    text-transform: uppercase;
    color: #333333;
    padding-bottom: 8px;
  }

  tbody {
    tr {
      td {
        padding-top: 22px;

        .title {
          font-weight: 500;
          font-size: 14px;
          line-height: 19px;
          color: #333333;
        }

        .value {
          font-weight: 300;
          font-size: 12px;
          line-height: 17px;
          color: #000000;
        }
      }
    }
  }
`;

const Slug = styled.div`
  font-size: 12px;
  line-height: 17px;
  margin-right: 12px;
  color: #4f31ff;
  cursor: pointer;
  max-width: 140px;
  display: inline-block;
  word-break: break-all;
`;

const BreakLine = styled.hr`
  border-top: 1px solid #efefef;
  margin: 24px 0 36px;
`;

const Indicator = styled.span`
  background-color: ${({ color }) => color};
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 8px;
`;

const AccountsCol = styled(Col)`
  margin-top: 36px;

  ${ControlLabel} label {
    font-size: 14px;
  }
`;

const AccountsRow = styled(Row)`
  flex-wrap: wrap;
  font-size: 12px;

  span {
    max-width: 200px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  ${Row} {
    margin-right: 20px;
  }
`;

export default ExternalCollabLink;
